<template>
  <v-card flat color="white">
    <v-toolbar flat color="primary" dark dense>
      <v-toolbar-title>{{ $translate('UPCOMING_ORDER') }}</v-toolbar-title>
      <v-spacer />
      <!-- Button to add products -->
      <v-btn color="secondary" class="text-capitalize" @click="onAddProduct">
        {{ $translate('ADD_PRODUCT') }}
      </v-btn>
    </v-toolbar>

    <v-card-text class="products pa-0">
      <v-list class="mb-2" dense>
        <template v-for="(product, productIndex) in products">
          <v-list-item
            :key="`product-${getProdUniqueId(product)}`"
            class="px-2"
          >
            <!-- Picture -->
            <v-list-item-avatar>
              <v-img
                max-width="35"
                max-height="35"
                :src="product.Product.PictureUrl"
              />
            </v-list-item-avatar>

            <!-- Name and defs -->
            <v-list-item-content>
              <v-list-item-title v-text="product.Product.Name" />
              <v-list-item-subtitle v-if="product.ItemDef">
                {{ product.ItemDef }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="product.OrderProductOptionsSummary">
                {{ product.OrderProductOptionsSummary }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <!-- Actions for non-mobile screen -->
            <v-list-item-action v-if="$vuetify.breakpoint.smAndUp">
              <ShoppingListProductActions
                :product="product"
                :totalPrice="getProductTotalPrice(product)"
                :quantity="
                  getDisplayQuantity(product.Product, product.Quantity)
                "
                :isWeighted="product.Product.Type === 'Weighted'"
                :unit="getDisplayUnit(product.Product)"
                :decreaseBtnDisabled="decreaseBtnDisabled(product)"
                :increaseBtnDisabled="increaseBtnDisabled(product)"
                @decrease="onDecreaseQuantityListItem(product)"
                @increase="onIncreaseQuantityListItem(product)"
                @delete="onDeleteBtnClick(product)"
              />
            </v-list-item-action>
          </v-list-item>

          <!-- Actions for mobile screen -->
          <v-row
            v-if="$vuetify.breakpoint.xsOnly"
            :key="`product-${getProdUniqueId(product)}-actions-xs`"
            class="ma-0 px-4 mb-2"
            justify="end"
          >
            <ShoppingListProductActions
              :product="product"
              :totalPrice="getProductTotalPrice(product)"
              :quantity="getDisplayQuantity(product.Product, product.Quantity)"
              :isWeighted="product.Product.Type === 'Weighted'"
              :unit="getDisplayUnit(product.Product)"
              :decreaseBtnDisabled="decreaseBtnDisabled(product)"
              :increaseBtnDisabled="increaseBtnDisabled(product)"
              @decrease="onDecreaseQuantityListItem(product)"
              @increase="onIncreaseQuantityListItem(product)"
              @delete="onDeleteBtnClick(product)"
            />
          </v-row>

          <!-- Option to edit item defs -->
          <v-card
            v-if="getSortedItemDefNames(product.Product.ItemDefs).length > 0"
            :key="`product-${getProdUniqueId(product)}-defs`"
            flat
            tile
            class="py-1"
          >
            <v-row class="ma-0 px-1" justify="space-between">
              <v-col
                v-for="itemName in getSortedItemDefNames(
                  product.Product.ItemDefs
                )"
                cols="12"
                md="6"
                class="pb-2 pt-0"
                :key="`product-${product.ProductVueKey}-${getProdUniqueId(
                  product
                )}-defs-${itemName}`"
              >
                <v-select
                  :value="product.ItemDefObj[itemName]"
                  :items="getSortedItemDefValues(product.Product, itemName)"
                  :label="itemName"
                  hide-details="auto"
                  dense
                  filled
                  @input="
                    (value) =>
                      onProductItemDefChange(productIndex, itemName, value)
                  "
                />
              </v-col>
            </v-row>
          </v-card>

          <v-divider
            v-if="productIndex < products.length - 1"
            :key="`product-${getProdUniqueId(product)}-divider`"
            class="mx-1"
          />
        </template>
      </v-list>
    </v-card-text>

    <!-- Dialog to show login info -->
    <ConfirmDialog
      :visible="showDialog"
      :title="dialogTitle"
      :message="dialogMessage"
      :negativeBtnText="dialogNegativeBtnText"
      :positiveBtnText="dialogPositiveBtnText"
      @negativeBtnClicked="dialogNegativeBtnCallback"
      @positiveBtnClicked="dialogPositiveBtnCallback"
    />
  </v-card>
</template>

<script>
import ShoppingListProductActions from '@/components/shoppingLists/ShoppingListProductActions';
import ConfirmDialog from '@/components/generalComponents/uiComponents/ConfirmDialog';
import {
  getSortedItemDefNames,
  getSortedItemDefValues,
  getItemByDefs,
  quantityDecreasable,
  quantityIncreasable,
  getDisplayQuantity,
  getDisplayUnit,
  getMinimumQuantity,
  getMaximumQuantity,
  hasCapacity,
  getCapacity,
} from '@/objects/Product';
import utility from '@/shared/utility';

export default {
  name: 'ShoppingListProducts',
  components: {
    ShoppingListProductActions,
    ConfirmDialog,
  },
  props: {
    shoppingList: Object,
  },
  emits: ['item-change', 'decrease', 'increase', 'delete'],
  computed: {
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
  },
  data() {
    return {
      products: [],

      // Dialog to confirm the change of quantity
      showDialog: false,
      dialogTitle: '',
      dialogMessage: '',
      dialogNegativeBtnText: this.$translate('NO'),
      dialogPositiveBtnText: this.$translate('YES'),
      dialogNegativeBtnCallback: {},
      dialogPositiveBtnCallback: {},
      productChanges: {},
    };
  },
  watch: {
    shoppingList() {
      this.initProducts();
    },
  },
  mounted() {
    this.initProducts();
  },
  methods: {
    getDisplayQuantity,
    getDisplayUnit,
    initProducts() {
      this.products = [];

      if (
        this.shoppingList == null ||
        Object.keys(this.shoppingList).length === 0
      ) {
        return;
      }

      switch (this.shoppingList.Type) {
        case 'WishList':
          // this.products = cloneDeep(this.shoppingList.Products);
          this.products = [...this.shoppingList.Products];
          break;
        default:
          this.shoppingList.OrderGroup.Orders.forEach((order) => {
            // this.products.push(...cloneDeep(order.Products));
            this.products.push(...order.Products);
          });
          break;
      }

      // For force update of vue component
      this.products.forEach((prod) => {
        if (prod.ProductVueKey == null) {
          prod.ProductVueKey = 0;
        }
      });
    },
    getSortedItemDefNames,
    getSortedItemDefValues,
    getProductTotalPrice(product) {
      if (!this.isProfUser) {
        return product.Price;
      }

      // Professional user with order info in shopping list
      if (product.ItemTotalPrice != null && product.ItemTotalVatPrice != null) {
        return product.ItemTotalPrice - product.ItemTotalVatPrice;
      }

      // Professional user without order info in shopping list
      const vatPrice = utility.getVatPrice(
        product.Price,
        product.Product.VatPercentage
      );
      return product.Price - vatPrice;
    },
    decreaseBtnDisabled(product) {
      return !quantityDecreasable(
        product.Product,
        getItemByDefs(product.Product, product.ItemDefObj),
        product.Quantity
      );
    },
    increaseBtnDisabled(product) {
      return !quantityIncreasable(
        product.Product,
        getItemByDefs(product.Product, product.ItemDefObj),
        product.Quantity
      );
    },
    onAddProduct() {
      this.$emit('add');
    },
    onProductItemDefChange(productIndex, defName, defValue) {
      /* Check if current quantity is between the min and max quantity of the new item
      If not in the range, show a warning message */
      const product = this.products[productIndex];

      const currentDefObj = product.ItemDefObj;
      const newDefObj = { ...currentDefObj };
      newDefObj[defName] = defValue;
      const newItem = getItemByDefs(product.Product, newDefObj);

      const currentQty = product.Quantity;
      const capacityManaged = product.Product.CapacityManaged;
      const capacityOfNewItem = getCapacity(product.Product, newItem);

      this.productChanges = {
        ProductIndex: productIndex,
        OldDefObj: currentDefObj,
        NewDefObj: newDefObj,
        NewQuantity: currentQty,
      };

      this.dialogNegativeBtnText = this.$translate('NO');
      this.dialogPositiveBtnText = this.$translate('YES');

      if (!hasCapacity(product, newItem)) {
        // New item is sold out. Inform user and roll back
        this.dialogTitle = this.$translate('PRODUCT_SOLD_OUT');
        this.dialogMessage = this.$translate('DEF_CHANGE_NO_CAPACITY_MSG');
        this.dialogNegativeBtnText = '';
        this.dialogPositiveBtnText = this.$translate('CLOSE');
        this.dialogPositiveBtnCallback = this.onNotUpdateQtyForDefChange;
        this.showDialog = true;
      } else if (capacityManaged && currentQty > capacityOfNewItem) {
        // Product is capacity managed, and current quantity is less than the new item's capacity
        this.productChanges.NewQuantity = capacityOfNewItem;
        this.dialogTitle = this.$translate('DEF_CHANGE_INVALID_QTY_TITLE');
        this.dialogMessage = utility.sprintf(
          this.$translate('DEF_CHANGE_QTY_LESS_THAN_CAPACITY_MSG'),
          capacityOfNewItem
        );
        this.dialogNegativeBtnCallback = this.onNotUpdateQtyForDefChange;
        this.dialogPositiveBtnCallback = this.onUpdateQtyForDefChange;
        this.showDialog = true;
      } else {
        // Current quantity is valid for the new item
        product.ItemDefObj[defName] = defValue;
        product.ProductVueKey += 1;
        this.$emit('item-change', product);
      }
    },
    onDeleteBtnClick(product) {
      this.$emit('delete', product);
    },
    onDecreaseQuantityListItem(product) {
      this.$emit('decrease', product);
    },
    onIncreaseQuantityListItem(product) {
      this.$emit('increase', product);
    },
    getProdUniqueId(product) {
      if (product.TemporaryOrderItemId) {
        // Unique id of the product for Wishlist (no order yet)
        return product.TemporaryOrderItemId;
      } else if (product.OrderItemId) {
        // Unique id of the product for ShoppingList (order already created)
        return product.OrderItemId;
      } else {
        // Should never happen
        return product.ItemId;
      }
    },
    closeDialog() {
      this.showDialog = false;
      this.dialogTitle = '';
      this.dialogMessage = '';
    },
    onNotUpdateQtyForDefChange() {
      // Roll back the changes to item def
      const index = this.productChanges.ProductIndex;
      const product = this.products[index];

      product.ItemDefObj = this.productChanges.OldDefObj;
      product.ProductVueKey += 1;

      this.productChanges = {};
      this.closeDialog();
    },
    onUpdateQtyForDefChange() {
      // Apply changes to item def and quantity
      const index = this.productChanges.ProductIndex;
      const product = this.products[index];

      product.ItemDefObj = this.productChanges.NewDefObj;
      product.Quantity = this.productChanges.NewQuantity;
      product.ProductVueKey += 1;

      this.$emit('item-change', product);

      this.productChanges = {};
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss" scoped>
.products {
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
